import React, { useState, useEffect, useRef } from 'react';
import { 
  Building2, 
  MapPin, 
  Search, 
  Check, 
  ChevronDown, 
  Navigation, 
  Loader2, 
  Sparkles, 
  X,
  Edit3
} from 'lucide-react';
import { US_STATES, getCitiesForState, getStateName } from '../../data/usLocations';
import { detectUserLocation } from '../../utils/locationDetector';

interface StateCitySelectorProps {
  selectedState: string;
  selectedCity: string;
  onStateChange: (newState: string, defaultCity: string) => void;
  onCityChange: (newCity: string) => void;
  stateLabel?: string;
  cityLabel?: string;
  containerClassName?: string;
  selectClassName?: string;
  labelClassName?: string;
  showIcons?: boolean;
  isRequired?: boolean;
  enableAutoDetectButton?: boolean;
  autoDetectOnMount?: boolean;
}

export const StateCitySelector: React.FC<StateCitySelectorProps> = ({
  selectedState,
  selectedCity,
  onStateChange,
  onCityChange,
  stateLabel = 'State 🏛️',
  cityLabel = 'City / Town 🏙️',
  containerClassName = 'grid grid-cols-1 sm:grid-cols-2 gap-3',
  selectClassName = 'w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400',
  labelClassName = 'text-xs font-bold text-indigo-200 block mb-1',
  showIcons = true,
  isRequired = true,
  enableAutoDetectButton = true,
  autoDetectOnMount = false,
}) => {
  // State combobox control
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const stateRef = useRef<HTMLDivElement>(null);

  // City combobox control
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const cityRef = useRef<HTMLDivElement>(null);

  // Geolocation detection status
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedStatus, setDetectedStatus] = useState<string | null>(null);

  const availableCities = getCitiesForState(selectedState);
  const currentStateObj = US_STATES.find((s) => s.code === selectedState) || US_STATES[0];

  // Filter states based on combobox search
  const filteredStates = US_STATES.filter(
    (st) =>
      st.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
      st.code.toLowerCase().includes(stateSearch.toLowerCase())
  );

  // Filter cities based on combobox search
  const filteredCities = availableCities.filter((city) =>
    city.toLowerCase().includes(citySearch.toLowerCase())
  );

  // Click outside listener to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (stateRef.current && !stateRef.current.contains(e.target as Node)) {
        setIsStateOpen(false);
      }
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) {
        setIsCityOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto detect location on mount if requested and state/city are default
  useEffect(() => {
    if (autoDetectOnMount && (!selectedState || selectedState === 'NY')) {
      handleAutoDetectLocation();
    }
  }, []);

  const handleAutoDetectLocation = async () => {
    setIsDetecting(true);
    setDetectedStatus(null);
    try {
      const res = await detectUserLocation();
      onStateChange(res.stateCode, res.cityName);
      onCityChange(res.cityName);
      setDetectedStatus(`Detected: ${res.cityName}, ${res.stateCode} 📍`);
    } catch (err: any) {
      setDetectedStatus(err.message || 'Could not detect location');
    } finally {
      setIsDetecting(false);
    }
  };

  const handleSelectState = (stateCode: string) => {
    const cities = getCitiesForState(stateCode);
    const defaultCity = cities[0] || 'Main City';
    onStateChange(stateCode, defaultCity);
    setIsStateOpen(false);
    setStateSearch('');
  };

  const handleSelectCity = (cityName: string) => {
    onCityChange(cityName);
    setIsCityOpen(false);
    setCitySearch('');
  };

  return (
    <div className="space-y-2">
      {/* Auto Detect Location Quick Trigger Banner */}
      {enableAutoDetectButton && (
        <div className="flex items-center justify-between gap-2 pb-1">
          <button
            type="button"
            onClick={handleAutoDetectLocation}
            disabled={isDetecting}
            className="px-3 py-1.5 bg-lime-400/15 hover:bg-lime-400/25 border border-lime-400/40 rounded-xl text-xs font-bold text-lime-300 flex items-center space-x-1.5 transition active:scale-95 disabled:opacity-50"
          >
            {isDetecting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-lime-400" />
            ) : (
              <Navigation className="w-3.5 h-3.5 text-lime-400" />
            )}
            <span>{isDetecting ? 'Detecting GPS City...' : 'Auto-Detect Current Location 📍'}</span>
          </button>

          {detectedStatus && (
            <span className="text-[10px] font-mono font-bold text-lime-400 truncate max-w-[200px]">
              {detectedStatus}
            </span>
          )}
        </div>
      )}

      <div className={containerClassName}>
        {/* SEARCHABLE STATE COMBOBOX */}
        <div ref={stateRef} className="relative">
          <label className={labelClassName}>
            <span className="flex items-center gap-1">
              {showIcons && <Building2 className="w-3.5 h-3.5 text-lime-400 shrink-0" />}
              <span>{stateLabel} {isRequired && '*'}</span>
            </span>
          </label>

          {/* Trigger Box */}
          <div
            onClick={() => {
              setIsStateOpen(!isStateOpen);
              setIsCityOpen(false);
            }}
            className={`${selectClassName} cursor-pointer flex items-center justify-between gap-2 transition hover:border-lime-400/50`}
          >
            <div className="flex items-center space-x-2 truncate">
              <span className="px-1.5 py-0.5 bg-lime-400/20 text-lime-300 rounded text-[10px] font-black font-mono">
                {selectedState || 'CA'}
              </span>
              <span className="truncate">{getStateName(selectedState)}</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-indigo-300 shrink-0 transition-transform ${isStateOpen ? 'rotate-180' : ''}`} />
          </div>

          {/* Searchable Dropdown Popup */}
          {isStateOpen && (
            <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-indigo-950 border border-lime-400/30 rounded-2xl shadow-2xl overflow-hidden p-2 space-y-2 max-h-72 flex flex-col backdrop-blur-xl">
              {/* Search Bar */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-indigo-300 absolute left-2.5 top-2.5 pointer-events-none" />
                <input
                  type="text"
                  autoFocus
                  placeholder="Search 50 States (e.g. TX, Florida)..."
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  className="w-full pl-8 pr-7 py-1.5 bg-indigo-900/90 border border-white/10 rounded-xl text-xs text-white placeholder-indigo-300/50 outline-none focus:ring-1 focus:ring-lime-400 font-medium"
                />
                {stateSearch && (
                  <button
                    type="button"
                    onClick={() => setStateSearch('')}
                    className="absolute right-2 top-2 text-indigo-300 hover:text-white"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* State List Options */}
              <div className="overflow-y-auto space-y-0.5 flex-1 pr-1 custom-scrollbar">
                {filteredStates.length > 0 ? (
                  filteredStates.map((st) => {
                    const isSelected = st.code === selectedState;
                    const cityCount = getCitiesForState(st.code).length;
                    return (
                      <button
                        key={st.code}
                        type="button"
                        onClick={() => handleSelectState(st.code)}
                        className={`w-full text-left px-2.5 py-2 rounded-xl text-xs font-semibold flex items-center justify-between transition ${
                          isSelected
                            ? 'bg-lime-400 text-black font-extrabold shadow-md'
                            : 'text-indigo-100 hover:bg-indigo-900/80 hover:text-white'
                        }`}
                      >
                        <div className="flex items-center space-x-2 truncate">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-black font-mono ${isSelected ? 'bg-black/20 text-black' : 'bg-indigo-900 text-lime-300'}`}>
                            {st.code}
                          </span>
                          <span className="truncate">{st.name}</span>
                        </div>
                        <div className="flex items-center space-x-1 shrink-0">
                          <span className={`text-[9px] ${isSelected ? 'text-black/70' : 'text-indigo-300'}`}>
                            {cityCount} cities
                          </span>
                          {isSelected && <Check className="w-3.5 h-3.5 ml-1" />}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="p-3 text-center text-xs text-indigo-300">
                    No state matching "{stateSearch}"
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* SEARCHABLE CITY COMBOBOX */}
        <div ref={cityRef} className="relative">
          <label className={labelClassName}>
            <span className="flex items-center gap-1">
              {showIcons && <MapPin className="w-3.5 h-3.5 text-lime-400 shrink-0" />}
              <span>{cityLabel} {isRequired && '*'}</span>
            </span>
          </label>

          {/* Trigger Box */}
          <div
            onClick={() => {
              setIsCityOpen(!isCityOpen);
              setIsStateOpen(false);
            }}
            className={`${selectClassName} cursor-pointer flex items-center justify-between gap-2 transition hover:border-lime-400/50`}
          >
            <div className="flex items-center space-x-2 truncate">
              <span className="truncate font-bold text-white">
                {selectedCity || availableCities[0] || 'Select City'}
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-indigo-300 shrink-0 transition-transform ${isCityOpen ? 'rotate-180' : ''}`} />
          </div>

          {/* Searchable Dropdown Popup */}
          {isCityOpen && (
            <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-indigo-950 border border-lime-400/30 rounded-2xl shadow-2xl overflow-hidden p-2 space-y-2 max-h-72 flex flex-col backdrop-blur-xl">
              {/* Search Bar */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-indigo-300 absolute left-2.5 top-2.5 pointer-events-none" />
                <input
                  type="text"
                  autoFocus
                  placeholder={`Search ${getStateName(selectedState)} cities, towns...`}
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="w-full pl-8 pr-7 py-1.5 bg-indigo-900/90 border border-white/10 rounded-xl text-xs text-white placeholder-indigo-300/50 outline-none focus:ring-1 focus:ring-lime-400 font-medium"
                />
                {citySearch && (
                  <button
                    type="button"
                    onClick={() => setCitySearch('')}
                    className="absolute right-2 top-2 text-indigo-300 hover:text-white"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Custom City Entry Option if user typed a non-listed search */}
              {citySearch.trim().length > 0 && !availableCities.some((c) => c.toLowerCase() === citySearch.toLowerCase()) && (
                <button
                  type="button"
                  onClick={() => handleSelectCity(citySearch.trim())}
                  className="w-full text-left p-2 bg-indigo-900/90 hover:bg-lime-400/20 border border-lime-400/40 rounded-xl text-xs font-bold text-lime-300 flex items-center space-x-2 transition"
                >
                  <Edit3 className="w-3.5 h-3.5 shrink-0 text-lime-400" />
                  <span className="truncate">Use Custom: "{citySearch.trim()}"</span>
                </button>
              )}

              {/* City List Options */}
              <div className="overflow-y-auto space-y-0.5 flex-1 pr-1 custom-scrollbar">
                {filteredCities.length > 0 ? (
                  filteredCities.map((city) => {
                    const isSelected = city.toLowerCase() === selectedCity.toLowerCase();
                    return (
                      <button
                        key={city}
                        type="button"
                        onClick={() => handleSelectCity(city)}
                        className={`w-full text-left px-2.5 py-1.5 rounded-xl text-xs font-semibold flex items-center justify-between transition ${
                          isSelected
                            ? 'bg-lime-400 text-black font-extrabold shadow-md'
                            : 'text-indigo-100 hover:bg-indigo-900/80 hover:text-white'
                        }`}
                      >
                        <span className="truncate">{city}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 shrink-0 ml-1" />}
                      </button>
                    );
                  })
                ) : (
                  <div className="p-3 text-center text-xs text-indigo-300">
                    No predefined city matching "{citySearch}". Click "Use Custom" above!
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
